import React, { useEffect, useRef, useState } from 'react'
import { Card } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { Controller, useForm } from 'react-hook-form';
import AgentSelectFilterComponent from './booking-filter/AgentSelectFilterComponent';
import OtaChannelSelectFilterComponent from './booking-filter/OtaChannelFilterComponent';
import { format } from 'date-fns';
import { DateRangePicker, DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { useMediaQuery } from '../hooks/useMediaQuery';

const dateTypeOptions = [
    { value: 'checkin', label: 'Checkin' },
    { value: 'checkout', label: 'Checkout' },
    { value: 'created_at', label: 'Created At' }
];

const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'modified', label: 'Modified' },
    { value: 'cancelled', label: 'Cancelled' }
];

const badgeOptions = [
    { color: 'success', label: 'Confirmed' },
    { color: 'warning', label: 'Modified' },
    { color: 'danger', label: 'Cancelled' }
]

export interface FilterProps {
    search: string;
    date_type?: string;
    property_id?: string;
    from?: string;
    to?: string;
    status?: string;
    agent?: string;
    show_cancel?: boolean;
    isAllProperties?: boolean;
    selectedProperties?: string[];
    ota_channel?: string;
}

interface ReservationFiltersProps {
    onFilter: (filter: FilterProps) => void;
}

const ReservationFilters: React.FC<ReservationFiltersProps> = ({ onFilter }) => {
    const isMobile = useMediaQuery('(max-width: 768px)');

    const { register, handleSubmit, control, reset, setValue, watch } = useForm<FilterProps>({
        defaultValues: {
            agent: '',
            ota_channel: '',
            from: format(new Date(), 'yyyy-MM-dd'),
            to: format(new Date(), 'yyyy-MM-dd'),
        }
    });

    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    ]);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    // ✨ BARU: Memantau nilai 'date_type'
    const dateType = watch('date_type');

    const calendarContainerStyle: React.CSSProperties = {
        position: 'absolute',
        zIndex: 1000,
        top: '100%',
        marginTop: '8px',
        border: '1px solid #dfe2e5',
        borderRadius: '8px',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
    };

    if (isMobile) {
        calendarContainerStyle.left = '50%';
        calendarContainerStyle.transform = 'translateX(-50%)';
    } else {
        calendarContainerStyle.right = 0;
    }

    const fromDate = watch('from');
    const toDate = watch('to');

    const handleFilterReset = (): void => {
        Swal.fire({
            icon: 'warning',
            title: 'Filter reset',
            showConfirmButton: false,
            timer: 1500
        });
        reset();
        const today = new Date();
        setDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
        setValue('from', format(today, 'yyyy-MM-dd'));
        setValue('to', format(today, 'yyyy-MM-dd'));
        handleFilterChange({
            search: '',
        });
    }

    function handleFilterChange(data: FilterProps) {
        // ✨ DIHAPUS: Validasi yang mengharuskan 'date_type' tidak lagi diperlukan.
        const payload: any = {
            search: data.search,
        };

        // ✨ DIPERBARUI: Payload tanggal hanya ditambahkan jika 'date_type' dipilih.
        if (data.date_type && data.from && data.to) {
            if (data.date_type === "checkin") {
                payload.checkin_date = {
                    gte: data.from,
                    lte: data.to,
                };
            } else if (data.date_type === "checkout") {
                payload.checkout_date = {
                    gte: data.from,
                    lte: data.to,
                };
            } else if (data.date_type === "created_at") {
                payload.created_at = {
                    gte: data.from,
                    lte: data.to,
                };
            }
        }

        if (data.status !== "all") {
            payload.status = data.status;
        }

        if (data.status === "all") {
            payload.status = ["confirmed", "modified"];
        } else {
            payload.status = [data.status];
        }

        if (data.agent !== "") {
            payload.agent_id = data.agent;
        }

        if (data.ota_channel !== "") {
            payload.ota_channel = data.ota_channel;
        }

        if (data.show_cancel) {
            payload.show_cancel = data.show_cancel;
        }

        Swal.fire({
            icon: "success",
            title: "Filter applied",
            showConfirmButton: false,
            timer: 1500,
        });

        onFilter(payload);
    }

    const handleDateChange = (rangesByKey: any) => {
        const { selection } = rangesByKey;
        if (selection.startDate && selection.endDate) {
            setDateRange([{
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: selection.key || 'selection'
            }]);
            setValue('from', format(selection.startDate, 'yyyy-MM-dd'));
            setValue('to', format(selection.endDate, 'yyyy-MM-dd'));
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [calendarRef]);

    return (
        <>
            <Card className="mb-3 shadow-none">
                <Card.Body>
                    <form onSubmit={handleSubmit(handleFilterChange)}>
                        <div className="row">
                            <div className='col-md-4'>
                                <div className='form-group'>
                                    <label htmlFor='search' className='mb-1'>Guest Name</label>
                                    <div className="input-icon">
                                        <input
                                            type="text"
                                            className="form-control app-form-control"
                                            placeholder="Search..."
                                            {...register('search')}
                                        />
                                        <span className="input-icon-addon">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <circle cx="10" cy="10" r="7" />
                                                <line x1="21" y1="21" x2="15" y2="15" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='col-md-4 mt-2 mt-md-0'>
                                <div className='form-group'>
                                    <label htmlFor='date_type' className='mb-1'>Date Type</label>
                                    <Controller
                                        name='date_type'
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                key={field.value}
                                                {...field}
                                                options={dateTypeOptions}
                                                value={dateTypeOptions.find((option) => option.value === field.value)}
                                                onChange={(selectedOption) => {
                                                    field.onChange(selectedOption?.value);
                                                }}
                                                isClearable // ✨ BARU: Membuat Select dapat dikosongkan
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4 mt-2 mt-md-0">
                                <div className="form-group">
                                    <label htmlFor="date" className="mb-1">Pick Date Range</label>
                                    <div className="position-relative" ref={calendarRef}>
                                        <div className="input-group">
                                            {/* ✨ DIPERBARUI: Input dinonaktifkan jika 'dateType' kosong */}
                                            <input
                                                type='text'
                                                readOnly
                                                className='form-control app-form-control'
                                                onClick={() => dateType && setIsCalendarOpen(!isCalendarOpen)}
                                                value={dateType ? `${format(new Date(fromDate || new Date()), 'dd MMM yyyy')} - ${format(new Date(toDate || new Date()), 'dd MMM yyyy')}` : ''}
                                                placeholder={dateType ? "Select date range" : "Select Date Type first"}
                                                disabled={!dateType}
                                            />
                                            {/* ✨ DIPERBARUI: Ikon kalender juga dinonaktifkan */}
                                            <span
                                                className="input-group-text"
                                                onClick={() => dateType && setIsCalendarOpen(!isCalendarOpen)}
                                                style={{ cursor: dateType ? 'pointer' : 'not-allowed', backgroundColor: !dateType ? '#e9ecef' : '#fff' }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                    <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                                                    <path d="M16 3l0 4"></path>
                                                    <path d="M8 3l0 4"></path>
                                                    <path d="M4 11l16 0"></path>
                                                    <path d="M8 15h2v2h-2z"></path>
                                                </svg>
                                            </span>
                                        </div>
                                        {isCalendarOpen && (
                                            <div style={calendarContainerStyle}>
                                                {isMobile ? (
                                                    <DateRange
                                                        onChange={handleDateChange}
                                                        moveRangeOnFirstSelection={false}
                                                        ranges={dateRange}
                                                        months={1}
                                                        direction="vertical"
                                                        className="shadow-sm"
                                                    />
                                                ) : (
                                                    <DateRangePicker
                                                        onChange={handleDateChange}
                                                        moveRangeOnFirstSelection={false}
                                                        ranges={dateRange}
                                                        months={2}
                                                        direction="horizontal"
                                                        inputRanges={[]}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-3 mt-2 mt-md-0">
                                <div className="form-group">
                                    <label htmlFor="status" className="mb-1">Status</label>
                                    <Controller
                                        name='status'
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                key={field.value}
                                                {...field}
                                                options={statusOptions}
                                                value={statusOptions.find((option) => option.value === field.value)}
                                                onChange={(selectedOption) => field.onChange(selectedOption?.value || 'all')}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3 mt-2 mt-md-0">
                                <div className='form-group'>
                                    <label htmlFor="agent_id" className="mb-1">Agent</label>
                                    <Controller
                                        name='agent'
                                        control={control}
                                        render={({ field }) => (
                                            <AgentSelectFilterComponent
                                                {...field}
                                                onChange={(selected) => {
                                                    if (selected.length === 0) {
                                                        field.onChange('');
                                                    } else {
                                                        field.onChange((selected?.map((s: any) => s).join(',')) || []);
                                                    }
                                                }}
                                                defaultValue={field.value}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className='col-md-3 mt-2 mt-md-0'>
                                <div className='form-group'>
                                    <label htmlFor='ota_channel' className='mb-1'>OTA Channel</label>
                                    <Controller
                                        name='ota_channel'
                                        control={control}
                                        render={({ field }) => (
                                            <OtaChannelSelectFilterComponent
                                                {...field}
                                                onChange={(selected) => {
                                                    if (selected.length === 0) {
                                                        field.onChange('');
                                                    } else {
                                                        field.onChange((selected?.map((s: any) => s).join(',')) || []);
                                                    }
                                                }}
                                                defaultValue={field.value}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3 mt-2 mt-md-0">
                                <div className="form-group text-start text-md-end">
                                    <label htmlFor="show_cancel" className="mb-1">Show Cancel Booking</label>
                                    <div className="form-check form-switch mb-0 mt-2 d-flex justify-content-start justify-content-md-end">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="show_cancel"
                                            {...register('show_cancel')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-12 d-flex align-items-center">
                                {badgeOptions.map((badge, index) => (
                                    <div key={index} className='d-flex align-items-center me-3'>
                                        <span className={`btn bg-${badge.color} p-2`}></span>
                                        <span className='fs-5 fw-bold ms-1'>{badge.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-3 mt-2 mt-md-0">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleFilterReset()}
                                >
                                    Reset Filter
                                </button>
                                <button type="submit" className="btn btn-primary btn-sm ms-2">
                                    Apply Filter
                                </button>
                            </div>
                        </div>
                    </form>
                </Card.Body>
            </Card>
        </>
    )
}

export default ReservationFilters